/** Client shapes of the server's entities (camelCase rows from /api/bootstrap and live deltas). */

export type Id = string;
export type Timestamp = string;
export type DateString = string;
export type RichText = { type: "doc"; content?: unknown[] } | null;

export type Role = "owner" | "admin" | "member" | "guest";
export type StateCategory = "triage" | "backlog" | "unstarted" | "started" | "completed" | "canceled";
export type Health = "on_track" | "at_risk" | "off_track";
export type ProjectStatus = "backlog" | "planned" | "started" | "paused" | "completed" | "canceled";
export type InitiativeStatus = "proposed" | "active" | "completed" | "canceled";
export type IdeaStatus = "inbox" | "exploring" | "shaped" | "promoted" | "parked" | "rejected";

interface Row {
  id: Id;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}
interface Archivable {
  archivedAt: Timestamp | null;
}

export interface Workspace extends Row {
  name: string;
  urlKey: string;
  allowedDomains: string[];
}

export interface User extends Row {
  email: string;
  name: string;
  avatarUrl: string | null;
  role: Role;
  active: boolean;
  whatsappLinked: boolean;
}

export interface Team extends Row, Archivable {
  key: string;
  name: string;
  description: string;
  color: string;
  icon: string | null;
  private: boolean;
  memberIds: Id[];
  defaultStateId: Id | null;
  triageEnabled: boolean;
  triageRotation: Id[];
  cyclesEnabled: boolean;
  cycleDurationWeeks: number;
  cycleStartWeekday: number;
  cycleUpcoming: number;
  cycleAutoRollover: boolean;
  estimateScale: "none" | "linear" | "fibonacci" | "tshirt";
  slaHours: Record<string, number>;
  autoArchiveDays: number;
  autoCloseStaleDays: number;
  autoCloseParent: boolean;
  gitPrOpenStateId: Id | null;
  gitPrMergedStateId: Id | null;
  intakeToken: string | null;
}

export interface WorkflowState extends Row, Archivable {
  teamId: Id;
  name: string;
  category: StateCategory;
  color: string;
  description: string;
  position: number;
}

export interface Label extends Row, Archivable {
  teamId: Id | null;
  parentId: Id | null;
  isGroup: boolean;
  name: string;
  color: string;
  description: string;
  scope: "issue" | "project" | "idea";
}

export interface IssueSource {
  kind: "form" | "email" | "whatsapp" | "github" | "api" | "mcp" | "import" | "recurring" | "automation";
  [key: string]: unknown;
}

export interface Issue extends Row, Archivable {
  teamId: Id;
  number: number | null;
  title: string;
  description: RichText;
  descriptionText: string;
  stateId: Id;
  priority: number;
  estimate: number | null;
  assigneeId: Id | null;
  creatorId: Id | null;
  parentId: Id | null;
  projectId: Id | null;
  milestoneId: Id | null;
  cycleId: Id | null;
  labelIds: Id[];
  subscriberIds: Id[];
  upvoterIds: Id[];
  downvoterIds: Id[];
  dueDate: DateString | null;
  sortKey: string;
  source: IssueSource | null;
  triagedAt: Timestamp | null;
  snoozedUntil: Timestamp | null;
  slaDueAt: Timestamp | null;
  slaBreachedAt: Timestamp | null;
  startedAt: Timestamp | null;
  completedAt: Timestamp | null;
  canceledAt: Timestamp | null;
  /** When the issue entered its current status. */
  stateChangedAt: Timestamp;
  lastEditedBy: Id | null;
  lastEditedAt: Timestamp | null;
}

export interface IssueRelation extends Row {
  type: "blocks" | "related" | "duplicate";
  issueId: Id;
  relatedIssueId: Id;
  teamId: Id | null;
  creatorId: Id | null;
}

export interface Comment extends Row, Archivable {
  issueId: Id | null;
  projectUpdateId: Id | null;
  ideaId: Id | null;
  documentId: Id | null;
  projectId: Id | null;
  parentId: Id | null;
  teamId: Id | null;
  authorId: Id | null;
  body: RichText;
  bodyText: string;
  quote: string | null;
  resolvedAt: Timestamp | null;
  editedAt: Timestamp | null;
}

export interface Reaction extends Row {
  commentId: Id | null;
  projectUpdateId: Id | null;
  userId: Id;
  emoji: string;
}

export interface Attachment extends Row, Archivable {
  issueId: Id | null;
  projectId: Id | null;
  ideaId: Id | null;
  uploaderId: Id | null;
  filename: string;
  contentType: string;
  size: number;
}

export interface Cycle extends Row, Archivable {
  teamId: Id;
  number: number;
  name: string | null;
  description: string;
  startsOn: DateString;
  endsOn: DateString;
  completedAt: Timestamp | null;
}

export interface Project extends Row, Archivable {
  name: string;
  summary: string;
  description: RichText;
  descriptionText: string;
  icon: string | null;
  color: string;
  status: ProjectStatus;
  priority: number;
  health: Health | null;
  leadId: Id | null;
  creatorId: Id | null;
  memberIds: Id[];
  teamIds: Id[];
  labelIds: Id[];
  startDate: DateString | null;
  targetDate: DateString | null;
  updateCadenceDays: number;
  lastUpdateAt: Timestamp | null;
  originIdeaId: Id | null;
  sortKey: string;
  startedAt: Timestamp | null;
  completedAt: Timestamp | null;
  canceledAt: Timestamp | null;
}

export interface ProjectMilestone extends Row, Archivable {
  projectId: Id;
  name: string;
  description: string;
  targetDate: DateString | null;
  sortKey: string;
}

export interface ProjectRelation extends Row {
  projectId: Id;
  relatedProjectId: Id;
  type: "blocks";
}

export interface ProjectUpdate extends Row, Archivable {
  projectId: Id | null;
  initiativeId: Id | null;
  health: Health;
  body: RichText;
  bodyText: string;
  authorId: Id | null;
}

export interface Initiative extends Row, Archivable {
  parentId: Id | null;
  name: string;
  summary: string;
  description: RichText;
  descriptionText: string;
  icon: string | null;
  color: string;
  status: InitiativeStatus;
  priority: number;
  health: Health | null;
  ownerId: Id | null;
  creatorId: Id | null;
  labelIds: Id[];
  projectIds: Id[];
  teamId: Id | null;
  targetDate: DateString | null;
  updateCadenceDays: number;
  lastUpdateAt: Timestamp | null;
  originIdeaId: Id | null;
  sortKey: string;
  completedAt: Timestamp | null;
}

export interface Document extends Row, Archivable {
  title: string;
  icon: string | null;
  projectId: Id | null;
  initiativeId: Id | null;
  ideaId: Id | null;
  teamId: Id | null;
  creatorId: Id | null;
  content: RichText;
  contentText: string;
  sortKey: string;
  lastEditedBy: Id | null;
  lastEditedAt: Timestamp | null;
}

export type StickyColor = "lavender" | "pink" | "peach" | "mint" | "sky" | "lemon" | "gray";

/** A personal note on Home and the Stickies page. Only its owner sees it. */
export interface Sticky extends Row {
  userId: Id;
  body: RichText;
  bodyText: string;
  color: StickyColor;
  sortKey: string;
}

/** A personal shortcut on Home: a Dayax path or an outside web address. */
export interface QuickLink extends Row {
  userId: Id;
  title: string;
  url: string;
  sortKey: string;
}

/** A new-issue form that was closed before it was sent. */
export interface IssueDraft extends Row {
  userId: Id;
  teamId: Id | null;
  title: string;
  description: RichText;
  descriptionText: string;
  data: Partial<Pick<Issue, "stateId" | "priority" | "assigneeId" | "labelIds" | "projectId" | "cycleId" | "estimate" | "dueDate" | "milestoneId" | "parentId">>;
}

export interface Idea extends Row, Archivable {
  number: number | null;
  title: string;
  summary: string;
  description: RichText;
  descriptionText: string;
  status: IdeaStatus;
  ownerId: Id | null;
  creatorId: Id | null;
  teamId: Id | null;
  labelIds: Id[];
  voteUserIds: Id[];
  scoreModel: "ice" | "rice";
  iceImpact: number | null;
  iceConfidence: number | null;
  iceEase: number | null;
  riceReach: number | null;
  riceImpact: number | null;
  riceConfidence: number | null;
  riceEffort: number | null;
  score: number | null;
  revisitOn: DateString | null;
  parkNote: string;
  promotedToType: "project" | "initiative" | null;
  promotedToId: Id | null;
  promotedAt: Timestamp | null;
  sortKey: string;
}

export interface IdeaDecision extends Row {
  ideaId: Id;
  decision: "approved" | "not_now" | "rejected";
  reason: string;
  decidedBy: Id | null;
}

export interface Customer extends Row, Archivable {
  name: string;
  domain: string | null;
  tier: string | null;
  revenueCents: number;
  ownerId: Id | null;
  notes: string;
}

export interface Evidence extends Row, Archivable {
  ideaId: Id | null;
  issueId: Id | null;
  projectId: Id | null;
  customerId: Id | null;
  teamId: Id | null;
  kind: "quote" | "ticket" | "link" | "slack" | "call" | "doc" | "email" | "other";
  url: string | null;
  quote: string;
  source: string;
  creatorId: Id | null;
}

export type Filter = { op: "and" | "or"; rules: Array<Filter | FilterRule> } | Record<string, never>;
export interface FilterRule {
  field: string;
  cmp: "in" | "notIn" | "empty" | "notEmpty" | "contains" | "before" | "after" | "lt" | "gt";
  value?: unknown;
}

export interface DisplayOptions {
  layout?: "list" | "board";
  groupBy?: GroupKey;
  subGroupBy?: GroupKey;
  orderBy?: "manual" | "priority" | "updated" | "created" | "dueDate" | "title" | "estimate";
  showSubIssues?: boolean;
  showEmptyGroups?: boolean;
  completed?: "all" | "week" | "month" | "none";
  properties?: string[];
}
export type GroupKey = "none" | "status" | "assignee" | "priority" | "project" | "cycle" | "label" | "team" | "milestone";

export interface View extends Row, Archivable {
  name: string;
  description: string;
  icon: string | null;
  color: string;
  ownerId: Id | null;
  teamId: Id | null;
  shared: boolean;
  entity: "issue" | "project" | "idea";
  filter: Filter;
  display: DisplayOptions;
  sortKey: string;
}

export interface Favorite extends Row {
  userId: Id;
  entity: string;
  entityId: Id;
  sortKey: string;
}

export interface DashboardWidget {
  id: string;
  kind: "number" | "bar" | "line" | "table";
  title: string;
  x: number;
  y: number;
  w: number;
  h: number;
  spec: InsightSpec;
}

export interface InsightSpec {
  measure: "issueCount" | "estimate" | "cycleTime" | "leadTime" | "triageTime" | "issueAge";
  stat?: "avg" | "median" | "p90";
  slice: string;
  segment?: string;
  bucket?: "day" | "week" | "month" | "quarter" | "year";
  filter?: Filter;
  range?: { field: string; from?: string; to?: string };
}

export interface Dashboard extends Row, Archivable {
  name: string;
  description: string;
  ownerId: Id | null;
  teamId: Id | null;
  shared: boolean;
  widgets: DashboardWidget[];
  sortKey: string;
}

export interface Template extends Row, Archivable {
  teamId: Id | null;
  name: string;
  description: string;
  data: Partial<Pick<Issue, "title" | "priority" | "labelIds" | "estimate" | "assigneeId" | "projectId">> & { descriptionText?: string };
  creatorId: Id | null;
}

export interface Recurrence extends Row, Archivable {
  teamId: Id;
  title: string;
  data: Template["data"];
  every: number;
  unit: "day" | "week" | "month";
  nextRunOn: DateString;
  active: boolean;
  lastIssueId: Id | null;
}

export interface FormField {
  key: string;
  label: string;
  kind: "text" | "textarea" | "select";
  required?: boolean;
  options?: string[];
}

export interface Form extends Row, Archivable {
  teamId: Id;
  name: string;
  description: string;
  fields: FormField[];
  labelIds: Id[];
  priority: number;
  enabled: boolean;
}

export interface Automation extends Row, Archivable {
  teamId: Id | null;
  name: string;
  trigger: { kind: string; [k: string]: unknown };
  conditions: FilterRule[];
  actions: Array<{ kind: string; value?: unknown }>;
  enabled: boolean;
  runCount: number;
  lastRunAt: Timestamp | null;
}

export interface Notification extends Row, Archivable {
  userId: Id;
  kind: string;
  entity: string;
  entityId: Id;
  actorId: Id | null;
  data: Record<string, unknown>;
  readAt: Timestamp | null;
  snoozedUntil: Timestamp | null;
}

export interface PullRequest extends Row {
  provider: "github" | "gitlab";
  repo: string;
  number: number;
  title: string;
  url: string;
  state: "open" | "draft" | "merged" | "closed";
  author: string;
  branch: string;
  issueIds: Id[];
  mergedAt: Timestamp | null;
}

export interface WhatsappSubscription extends Row {
  userId: Id;
  teamId: Id | null;
  projectId: Id | null;
  events: string[];
}

export interface Webhook extends Row, Archivable {
  label: string;
  url: string;
  secret: string;
  entities: string[];
  enabled: boolean;
}

export interface EntityMap {
  workspace: Workspace;
  user: User;
  team: Team;
  workflowState: WorkflowState;
  label: Label;
  issue: Issue;
  issueRelation: IssueRelation;
  comment: Comment;
  reaction: Reaction;
  attachment: Attachment;
  cycle: Cycle;
  project: Project;
  projectMilestone: ProjectMilestone;
  projectRelation: ProjectRelation;
  projectUpdate: ProjectUpdate;
  initiative: Initiative;
  document: Document;
  idea: Idea;
  ideaDecision: IdeaDecision;
  customer: Customer;
  evidence: Evidence;
  view: View;
  favorite: Favorite;
  dashboard: Dashboard;
  template: Template;
  recurrence: Recurrence;
  form: Form;
  automation: Automation;
  notification: Notification;
  pullRequest: PullRequest;
  whatsappSubscription: WhatsappSubscription;
  webhook: Webhook;
  sticky: Sticky;
  quickLink: QuickLink;
  issueDraft: IssueDraft;
}

export type EntityName = keyof EntityMap;
export const ENTITY_NAMES = [
  "workspace", "user", "team", "workflowState", "label", "issue", "issueRelation", "comment", "reaction", "attachment", "cycle", "project",
  "projectMilestone", "projectRelation", "projectUpdate", "initiative", "document", "idea", "ideaDecision", "customer", "evidence", "view",
  "favorite", "dashboard", "template", "recurrence", "form", "automation", "notification", "pullRequest", "whatsappSubscription", "webhook",
  "sticky", "quickLink", "issueDraft",
] as const satisfies readonly EntityName[];

export interface Me {
  user: { id: Id; email: string; name: string; avatarUrl: string | null; whatsappPhone: string | null; whatsappLinkedAt: string | null };
  membership: {
    workspace: { id: Id; name: string; urlKey: string };
    role: Role;
    emailNotifications: boolean;
    whatsappNotifications: boolean;
    whatsappKinds: string[];
  } | null;
  canCreateWorkspace: boolean;
  features: { ai: boolean; whatsapp: { number: string | null } | null; github: boolean; email: boolean; inboundEmail: boolean; inboundDomain?: string | null };
}

export type Op =
  | { op: "create"; entity: EntityName; id: Id; data: Record<string, unknown> }
  | { op: "update"; entity: EntityName; id: Id; data: Record<string, unknown> }
  | { op: "add" | "remove"; entity: EntityName; id: Id; field: string; values: Id[] }
  | { op: "archive" | "unarchive" | "delete"; entity: EntityName; id: Id }
  | { op: "action"; name: string; args: Record<string, unknown> };

export interface BatchResult {
  lastSeq: number;
  changed: number;
  duplicate: boolean;
  results: unknown[];
}
